"use client";
import { cn, withUrl } from "@/lib/utils";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useCookieStore } from "@/store/cookie-store";
import SecondaryButton from "@/components/ui/secondary-button";
import LINKS from "@/lib/links";

export interface CookieBannerProps {
  className?: string;
}

export default function CookieBanner({ className = "" }: CookieBannerProps) {
  const cookieAccepted = useCookieStore((state) => state.cookieAccepted);
  const toggleCookieAccepted = useCookieStore(
    (state) => state.toggleCookieAccepted
  );
  const toggleAnalyticsAccepted = useCookieStore(
    (state) => state.toggleAnalyticsAccepted
  );
  const [_, setHeight] = useState(0);
  const ref = useRef(null);

  const closeBanner = () => {
    setTimeout(() => {
      // When accepting cookies, also accept analytics
      toggleCookieAccepted(true);
      toggleAnalyticsAccepted(true);
    }, 300);
  };

  useEffect(() => {
    const handleResize = () => {
      const rect =
        ref.current && (ref.current as HTMLElement).getBoundingClientRect();
      if (rect) setHeight((rect as DOMRect).height);
    };

    // Call on component load
    handleResize();

    // Call on resize (respond to breakpoints)
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <div
        className={cn(
          "fixed bottom-0 z-40 m-0 w-full border-t border-t-secondary-600 bg-white transition-opacity duration-500",
          cookieAccepted ? "hidden" : "block"
        )}
        ref={ref}
      >
        <div
          className={cn(
            "mx-auto flex max-w-lg flex-col items-center justify-between gap-4 px-4 py-2 text-center md:p-6 lg:flex-row lg:justify-start lg:px-3 lg:py-8 lg:text-start",
            className
          )}
        >
          <h3 className="font-montserrat text-subheading-3-400 text-neutral-500 md:max-w-[723px]">
            We use cookies in this website. Other categories of cookies maybe
            used to personalize your experience, to distribute personalized
            commercial offers or to carry out analyses to optimize our offer.
            Your consent can be withdrawn at any time via the link in our data
            protection policy, Read our{" "}
            <Link
              className="underline"
              href={withUrl(LINKS.privacyPolicy.href)}
              prefetch
            >
              Privacy Policy
            </Link>
            .
          </h3>
          <SecondaryButton
            color="gray"
            size="md"
            onClick={closeBanner}
            id="cookie-accept-button"
            aria-label="Accept Cookies"
          >
            ACCEPT
          </SecondaryButton>
        </div>
      </div>
      {/* Add padding to end of page so all information is scrollable and never just blocked. */}
      <div
        className={cn(
          "w-full transition-all duration-500",
          cookieAccepted ? "hidden" : "block"
        )}
      ></div>
    </>
  );
}
